'use client';

import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../../store/auth-store';
import { Icon } from '@/src/components/ui/Icon';
import { FormWrapper } from '../FormWrapper';
import { Button } from '@workspace/ui/components/button';
import { Label } from '@workspace/ui/components/label';
import { Input } from '@workspace/ui/components/input';
import { GoogleButton, OrDivider } from '../GoogleButton';
import { Toaster } from '@workspace/ui/components/sonner';
import { CountryDropdown } from '@/src/components/ui/CountryDropdown';
import { uniqueLinkRegex } from '@/src/constants/constants';

const schema = z.object({
  countryCode: z.string(),
  email: z.string().email(),
  password: z.string().min(6),
});

type FormData = z.infer<typeof schema>;

export const CredentialsStep = ({ onNext }: { onNext: () => void }) => {
  const { data, setData } = useAuthStore();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: data.email || '',
      password: data.password || '',
      countryCode: data.countryCode || '',
    },
  });

  const formEmail = watch('email');
  const password = watch('password');
  const countryCode = watch('countryCode');

  useEffect(() => {
    setData({ email: formEmail, password, countryCode });
  }, [formEmail, password, countryCode, setData]);

  const prevStepsValid = useMemo(() => {
    const ul = (data.uniqueLink ?? '').trim().toLowerCase();
    const fn = (data.firstName ?? '').trim();
    const ln = (data.lastName ?? '').trim();
    const cc = (data.countryCode ?? '').trim();

    const uniqueLinkOk = ul.length >= 3 && uniqueLinkRegex.test(ul);
    const namesOk = fn.length >= 2 && ln.length >= 2;
    if (!cc) return false;

    return uniqueLinkOk && namesOk;
  }, [data.uniqueLink, data.firstName, data.lastName, data.countryCode]);

  const isFilledLocal = !!formEmail && !!password && !!countryCode;

  const onSubmit = (values: FormData) => {
    if (!prevStepsValid) return;
    setData(values);
    onNext();
  };

  return (
    <>
      <Toaster
        position="top-center"
        toastOptions={{
          className:
            'top-full md:mt-175 md:-ml-97 min-w-[200px] md:max-w-[500px]',
        }}
      />
      <FormWrapper title="Enter your details" personalLink={data.uniqueLink}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full xl:md:px-28 lg:px-10"
        >
          <Label className="w-full relative flex flex-col items-start text-[16px]">
            <CountryDropdown
              value={countryCode}
              onChange={(alpha2) => setValue('countryCode', alpha2)}
            />
            {errors.countryCode && (
              <p className="text-red-error md:text-sm text-xs font-light flex items-center gap-2.5 ">
                <Icon name="exclamation-mark" className="size-4" />
                {errors.countryCode.message}
              </p>
            )}
          </Label>
          <Label className="w-full relative flex flex-col items-start text-[16px] md:mt-3 mt-2">
            <Input
              type="email"
              className="border w-full rounded-md text-gray-text placeholder:text-gray-text md:placeholder:text-[16px] md:h-[51px] h-10 font-light md:text-[16px] text-xs"
              {...register('email')}
              placeholder="Enter your email"
              aria-invalid={!!errors.email}
            />
            {errors.email && (
              <p className="text-red-error md:text-sm text-xs font-light flex items-center gap-2.5 ">
                <Icon name="exclamation-mark" className="size-4" />
                {errors.email.message}
              </p>
            )}
          </Label>
          <Label className="w-full relative flex flex-col items-start text-[16px] md:mt-3 mt-2">
            <Input
              type="password"
              className="border w-full rounded-md placeholder:text-gray-text md:placeholder:text-[16px] md:h-[51px] h-10 font-light md:text-[16px] text-xs"
              {...register('password')}
              placeholder="Enter your password"
              aria-invalid={!!errors.password}
            />
            {errors.password && (
              <p className="text-red-error md:text-sm text-xs font-light flex items-center gap-2.5 ">
                <Icon name="exclamation-mark" className="size-4" />
                {errors.password.message}
              </p>
            )}
          </Label>
          <Button
            className="w-full md:mt-8 mt-5 md:h-[51px] h-10"
            disabled={
              !prevStepsValid ||
              !isFilledLocal ||
              !!errors.email ||
              !!errors.password
            }
          >
            Next
          </Button>
          <OrDivider />
          <GoogleButton
            name="Sign up"
            isDisabled={!prevStepsValid}
            disabledReason="Finish earlier steps first. Don’t forget to choose your country."
          />
        </form>
      </FormWrapper>
    </>
  );
};
